import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

const names = new Intl.DisplayNames(["fr"], { type: "region" });
const telephoneCountries = getCountries();
// Territories without a dedicated numbering plan still belong in the country list.
const extraRegions: Record<string, string> = {
  AQ: "",
  BV: "+47",
  GS: "+500",
  HM: "",
  PN: "+64",
  TF: "+262",
  UM: "+1",
};
const territoryPlans: Record<string, CountryCode> = {
  BV: "NO",
  GS: "FK",
  PN: "NZ",
  TF: "RE",
  UM: "US",
};

export const WORLD_COUNTRIES = [
  ...telephoneCountries.map((code) => ({
    code: String(code),
    name: names.of(code) ?? code,
    dial: `+${getCountryCallingCode(code)}`,
  })),
  ...Object.entries(extraRegions).map(([code, dial]) => ({
    code,
    name: names.of(code) ?? code,
    dial,
  })),
].sort((a, b) => a.name.localeCompare(b.name, "fr"));

export function countryInfo(code: string) {
  return WORLD_COUNTRIES.find((c) => c.code === code.toUpperCase());
}

export function countryFlagCode(code: string) {
  const aliases: Record<string, string> = { AC: "sh-ac", TA: "sh-ta" };
  return aliases[code.toUpperCase()] ?? code.toLowerCase();
}

/** Match French names with or without accents, ISO codes or calling codes. */
export function matchesCountrySearch(
  country: { name: string; code: string; dial?: string },
  query: string,
) {
  const normalize = (value: string) =>
    value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return normalize(`${country.name} ${country.code} ${country.dial ?? ""}`)
    .includes(normalize(query.trim()));
}

/** The separate prefix is authoritative: never silently send another country's number. */
export function registrationPhone(country: string, input: string): string | null {
  const region = countryInfo(country);
  const plan = territoryPlans[country] ?? country as CountryCode;
  if (!region?.dial || !telephoneCountries.includes(plan))
    return null;
  const raw = input.trim();
  const international = raw.startsWith("+") || raw.startsWith("00");
  const number = parsePhoneNumberFromString(
    raw.startsWith("00") ? `+${raw.slice(2)}` : raw,
    plan,
  );
  if (!number?.isValid()) return null;
  if (international && number.country !== plan) return null;
  if (number.country && number.country !== plan) return null;
  return number.number;
}
